/**
 * useFunnelPage — orquestra CRUD, remoção de etapa e navegação do funil.
 */
import { useState, useCallback } from 'react';
import { useAppContext } from '../../../../contexts/AppContext';
import { useLeads } from '../../../../contexts/leads/LeadsContext';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { notify } from '../../../../utils/toast';
import { repositories } from '../../../../infrastructure/di/crmContainer';
import { markFunnelCustomized } from '../../../onboarding/hooks/useOnboardingProgress';
import { UseFunnelFormReturn } from './useFunnelForm';
import { useFunnelUrlSync } from './useFunnelUrlSync';
import {
    deleteRemovedStages,
    persistStages,
    refreshFunnelData,
    resolvePersistedOrTempId,
    tempStageId,
} from './funnelPersist';

interface UseFunnelPageProps {
    form: UseFunnelFormReturn;
    selectedFunnelId: string | null;
    isEditMode: boolean;
}

interface UseFunnelPageReturn {
    isSubmitting: boolean;
    handleBack: () => void;
    handleSave: () => Promise<void>;
    handleTitleBlur: () => Promise<void>;
    handleDuplicateFunnel: () => Promise<void>;
    handleDeleteFunnel: () => Promise<void>;
    handleRemoveStage: (index: number) => Promise<void>;
}

export const useFunnelPage = ({
    form,
    selectedFunnelId,
    isEditMode,
}: UseFunnelPageProps): UseFunnelPageReturn => {
    const { currentPage, setCurrentPage } = useAppContext();
    const {
        createFunnel,
        updateFunnel,
        deleteFunnel,
        saveFunnelStage,
        deleteFunnelStage,
        funnelStages,
        funnels,
        refreshFunnels,
        refreshLeads,
        refreshStages,
        setSelectedFunnelId,
    } = useLeads();
    const { currentOrganization } = useOrganization();
    const [isSubmitting, setIsSubmitting] = useState(false);

    useFunnelUrlSync(currentPage, selectedFunnelId, setSelectedFunnelId);

    const goToKanban = () => setCurrentPage('kanban');

    const markOnboardingFunnelStep = async () => {
        if (!currentOrganization?.id) return;
        await markFunnelCustomized(currentOrganization.id);
    };

    const handleBack = goToKanban;

    const handleSave = async () => {
        if (!form.isValid()) {
            notify.error(form.getErrors()[0] || 'Dados inválidos.');
            return;
        }

        setIsSubmitting(true);
        try {
            const data = form.getFormData();
            const editing = Boolean(isEditMode && selectedFunnelId);
            let funnelId: string | null = editing ? selectedFunnelId : null;

            if (editing && selectedFunnelId) {
                await updateFunnel(selectedFunnelId, data.name);
                await deleteRemovedStages(deleteFunnelStage, funnelStages, data.stages);
            } else {
                const created = await createFunnel(data.name);
                if (created) funnelId = created.id;
            }

            if (funnelId) {
                await persistStages(
                    saveFunnelStage,
                    funnelId,
                    data.stages,
                    editing ? resolvePersistedOrTempId : () => tempStageId(),
                );
                if (!editing) setSelectedFunnelId(funnelId);
                await refreshFunnelData(refreshStages, refreshLeads, funnelId);
                notify.success(editing ? 'Funil atualizado com sucesso!' : 'Funil criado com sucesso!');
                if (!editing || form.isDirty) await markOnboardingFunnelStep();
            }

            await refreshFunnels();
            goToKanban();
        } catch (error) {
            console.error('Erro ao salvar funil:', error);
            notify.error('Erro ao salvar funil. Tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleTitleBlur = async () => {
        if (!selectedFunnelId || !form.name?.trim()) return;

        const currentFunnel = funnels.find((f) => f.id === selectedFunnelId);
        if (currentFunnel && currentFunnel.name === form.name) return;

        try {
            await updateFunnel(selectedFunnelId, form.name);
            notify.success('Nome do funil atualizado');
            await markOnboardingFunnelStep();
        } catch (error) {
            console.error('Erro ao atualizar nome do funil:', error);
            notify.error('Erro ao atualizar nome');
        }
    };

    const handleDuplicateFunnel = async () => {
        setIsSubmitting(true);
        try {
            const data = form.getFormData();
            const newFunnel = await createFunnel(`${data.name} (Cópia)`);

            if (newFunnel) {
                // Sem deadline — comportamento atual da duplicata
                await Promise.all(
                    data.stages.map((stage, index) =>
                        saveFunnelStage({
                            id: tempStageId(),
                            name: stage.name,
                            conversionEvent: stage.conversionEvent,
                            color: stage.color || null,
                            funnelId: newFunnel.id,
                            order: index,
                        }),
                    ),
                );

                await refreshFunnelData(refreshStages, refreshLeads, newFunnel.id);
                notify.success('Funil duplicado com sucesso!');
                await markOnboardingFunnelStep();
            }
        } catch (error) {
            console.error('Erro ao duplicar:', error);
            notify.error('Erro ao duplicar funil.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteFunnel = async () => {
        if (!selectedFunnelId) return;

        const confirmed = await notify.confirm('Excluir funil permanentemente?', {
            description: 'Esta ação não pode ser desfeita. Todos os dados do funil serão perdidos.',
            confirmText: 'Excluir',
            cancelText: 'Cancelar',
        });

        if (!confirmed) return;

        setIsSubmitting(true);
        try {
            await deleteFunnel(selectedFunnelId);
            notify.success('Funil excluído com sucesso!');
            goToKanban();
        } catch (error) {
            console.error('Erro ao excluir:', error);
            notify.error('Erro ao excluir funil.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveStage = useCallback(async (index: number) => {
        const stageToRemove = form.stageList[index];

        if (stageToRemove.id && !stageToRemove.id.startsWith('temp_')) {
            try {
                const leadsInStage = await repositories.lead.findByStage(stageToRemove.id);

                if (leadsInStage.length > 0) {
                    notify.warning(
                        `Não é possível deletar a etapa "${stageToRemove.name}" pois existem ${leadsInStage.length} negociação(ões) nela. ` +
                            'Mova as negociações para outra etapa antes de deletar.',
                    );
                    return;
                }
            } catch (error) {
                console.error('Erro ao validar remoção de etapa:', error);
                notify.error('Erro ao validar remoção da etapa.');
                return;
            }
        }

        form.removeStage(index);
    }, [form]);

    return {
        isSubmitting,
        handleBack,
        handleSave,
        handleTitleBlur,
        handleDuplicateFunnel,
        handleDeleteFunnel,
        handleRemoveStage,
    };
};

export default useFunnelPage;
